/**
 * Utility functions for safely accessing Sanity data fields
 * Handles conditional fields that may not exist in the GraphQL schema
 */

/**
 * Safely get a nested property from an object
 * @param {Object} obj - The object to access
 * @param {string} path - Dot notation path to the property
 * @param {*} defaultValue - Default value if property doesn't exist
 * @returns {*} The property value or default value
 */
export const safeGet = (obj, path, defaultValue = null) => {
  if (!obj || !path) return defaultValue;

  const keys = path.split(".");
  let result = obj;

  for (const key of keys) {
    if (result && typeof result === "object" && key in result) {
      result = result[key];
    } else {
      return defaultValue;
    }
  }

  return result !== undefined && result !== null ? result : defaultValue;
};

/**
 * Check if a field exists and has a value
 * @param {Object} obj - The object to check
 * @param {string} path - Dot notation path to the property
 * @returns {boolean} True if field exists and has a truthy value
 */
export const hasField = (obj, path) => {
  const value = safeGet(obj, path);
  return value !== null && value !== undefined && value !== "";
};

/**
 * Get asset URL from Sanity image/file asset
 * @param {Object} asset - Sanity asset object
 * @param {string} fallback - Fallback URL if asset doesn't exist
 * @returns {string} Asset URL or fallback
 */
export const getAssetUrl = (asset, fallback = "") => {
  return safeGet(asset, "asset.url", fallback);
};

/**
 * Get image alt text with fallback
 * @param {Object} image - Sanity image object
 * @param {string} fallback - Fallback alt text
 * @returns {string} Alt text or fallback
 */
export const getImageAlt = (image, fallback = "") => {
  return safeGet(image, "alt", fallback);
};

/**
 * Check if CTA is enabled and has required fields
 * @param {Object} cta - CTA object
 * @returns {boolean} True if CTA is valid and enabled
 */
export const isValidCta = (cta) => {
  if (!cta) return false;
  return (
    safeGet(cta, "enabled", false) &&
    hasField(cta, "text") &&
    hasField(cta, "link")
  );
};

/**
 * Get video URL from heroVideo object (either URL or file)
 * @param {Object} heroVideo - Hero video object
 * @returns {string|null} Video URL or null
 */
export const getVideoUrl = (heroVideo) => {
  if (!heroVideo) return null;

  // Check for direct video URL first
  const directUrl = safeGet(heroVideo, "videoUrl");
  if (directUrl) return directUrl;

  // Check for uploaded video file
  const fileUrl = safeGet(heroVideo, "videoFile.asset.url");
  if (fileUrl) return fileUrl;

  return null;
};

/**
 * Get poster image URL for video
 * @param {Object} heroVideo - Hero video object
 * @returns {string|null} Poster image URL or null
 */
export const getVideoPosterUrl = (heroVideo) => {
  return getAssetUrl(safeGet(heroVideo, "posterImage"), null);
};

/**
 * Check if media type is video and has valid video data
 * @param {Object} heroSection - Hero section data
 * @returns {boolean} True if valid video
 */
export const hasValidVideo = (heroSection) => {
  if (safeGet(heroSection, "mediaType") !== "video") return false;
  const videoUrl = getVideoUrl(safeGet(heroSection, "heroVideo"));
  return videoUrl !== null;
};

/**
 * Check if media type is image and has valid image data
 * @param {Object} heroSection - Hero section data
 * @returns {boolean} True if valid image
 */
export const hasValidImage = (heroSection) => {
  if (safeGet(heroSection, "mediaType") !== "image") return false;
  const imageUrl = getAssetUrl(safeGet(heroSection, "heroImage"));
  return imageUrl !== "";
};

/**
 * Render a single Portable Text span with marks (links, bold, italic, etc.)
 * @param {Object} child - Text span object with text and marks
 * @param {Array} markDefs - Array of mark definitions (links, etc.)
 * @param {number} key - React key for the element
 * @returns {React.Element|string} Rendered span with marks applied
 */
export const renderPortableTextSpan = (child, markDefs = [], key) => {
  const React = require("react");

  if (!child || !child.text) return "";

  // If no marks, return plain text
  if (!child.marks || child.marks.length === 0) {
    return child.text;
  }

  let element = child.text;

  // Process each mark
  child.marks.forEach((mark, index) => {
    // Check if it's a reference to a mark definition (like links)
    const markDef = markDefs?.find((def) => def._key === mark);

    if (markDef && markDef._type === "link") {
      // Render link
      element = (
        <a
          key={`${key}-link-${index}`}
          href={markDef.href}
          target={markDef.blank ? "_blank" : "_self"}
          rel={markDef.blank ? "noopener noreferrer" : undefined}
        >
          {element}
        </a>
      );
    } else {
      // Handle inline marks (strong, em, code, etc.)
      switch (mark) {
        case "strong":
          element = <strong key={`${key}-strong-${index}`}>{element}</strong>;
          break;
        case "em":
          element = <em key={`${key}-em-${index}`}>{element}</em>;
          break;
        case "code":
          element = <code key={`${key}-code-${index}`}>{element}</code>;
          break;
        case "underline":
          element = <u key={`${key}-u-${index}`}>{element}</u>;
          break;
        case "strike-through":
          element = <del key={`${key}-del-${index}`}>{element}</del>;
          break;
        default:
          // Unknown mark, keep as is
          break;
      }
    }
  });

  return element;
};

/**
 * Render Portable Text blocks to React elements
 * @param {Array} blocks - Array of Portable Text blocks
 * @param {string} className - Optional CSS class for paragraphs
 * @returns {Array<React.Element>} Array of rendered React elements
 */
export const renderPortableText = (blocks, className = "") => {
  const React = require("react");

  if (!blocks || !Array.isArray(blocks)) return null;

  return blocks.map((block, blockIndex) => {
    // Handle different block types
    switch (block._type) {
      case "block":
        // Regular paragraph or heading
        const children = block.children?.map((child, childIndex) => (
          <React.Fragment key={`${blockIndex}-${childIndex}`}>
            {renderPortableTextSpan(
              child,
              block.markDefs,
              `${blockIndex}-${childIndex}`
            )}
          </React.Fragment>
        ));

        // Render based on style
        switch (block.style) {
          case "h1":
            return (
              <h1 key={blockIndex} className={className}>
                {children}
              </h1>
            );
          case "h2":
            return (
              <h2 key={blockIndex} className={className}>
                {children}
              </h2>
            );
          case "h3":
            return (
              <h3 key={blockIndex} className={className}>
                {children}
              </h3>
            );
          case "h4":
            return (
              <h4 key={blockIndex} className={className}>
                {children}
              </h4>
            );
          case "h5":
            return (
              <h5 key={blockIndex} className={className}>
                {children}
              </h5>
            );
          case "h6":
            return (
              <h6 key={blockIndex} className={className}>
                {children}
              </h6>
            );
          case "blockquote":
            return (
              <blockquote key={blockIndex} className={className}>
                {children}
              </blockquote>
            );
          case "normal":
          default:
            return (
              <p key={blockIndex} className={className}>
                {children}
              </p>
            );
        }

      default:
        // Unknown block type, skip
        return null;
    }
  });
};

/**
 * Convert Portable Text to plain text (no formatting)
 * @param {Array} blocks - Array of Portable Text blocks
 * @returns {string} Plain text string
 */
export const portableTextToPlainText = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return "";

  return blocks
    .map((block) => {
      if (block._type !== "block" || !block.children) return "";
      return block.children.map((child) => child.text).join("");
    })
    .join("\n\n");
};
